import SharedConfig from '@packages/systems/core/constants/SharedConfig';

export const OPERATOR_EQ = '==';
export const OPERATOR_NE = '!=';
export const OPERATOR_GT = '>';
export const OPERATOR_LT = '<';
export const OPERATOR_GTE = '>=';
export const OPERATOR_LTE = '<=';
export const OPERATOR_EXISTS = 'exists';
export const OPERATOR_NOT_EXISTS = 'notexists';
export const OPERATOR_IDIN = 'idin';
export const OPERATOR_IDNIN = 'idnin';
export const OPERATOR_TRUE = 'true';
export const OPERATOR_FALSE = 'false';

const DYN_CONTEXT = SharedConfig.dynamicContextConstant;

export type FormatCallback = (options: {
  value: string | number | null;
  value2: string | number | null;
  value3: string | number | null;
  type: string;
}) => {
  operatorName: string;
  value: string | number;
};

function applyInvalidOperator(op: string) {
  return function () {
    // Tried to apply an operator that should never have been output from param.formatValue (i.e.)
    throw new Error(
      'tried to apply inapplicable operator: ' + JSON.stringify(op)
    );
  };
}

export const OPERATORS = {
  [OPERATOR_EQ]: {
    id: OPERATOR_EQ,
    name: 'eq',
    humanName: 'equals',
    format: function (options) {
      const {type, value} = options;
      const formattedValue = type === 'Id' ? DYN_CONTEXT : (value ?? '');

      return {operatorName: 'eq', value: formattedValue};
    },
    apply: function (a: string | number, b: string | number) {
      return a == b; // eslint-disable-line eqeqeq
    },
  },
  [OPERATOR_NE]: {
    id: OPERATOR_NE,
    name: 'ne',
    humanName: 'does not equal',
    format: function (options) {
      const {type, value} = options;
      const formattedValue = type === 'Id' ? DYN_CONTEXT : (value ?? '');

      return {operatorName: 'ne', value: formattedValue};
    },
    apply: function (a: string | number, b: string | number) {
      return a != b; // eslint-disable-line eqeqeq
    },
  },
  [OPERATOR_GT]: {
    id: OPERATOR_GT,
    name: 'gt',
    humanName: 'is greater than',
    apply: function (a: number, b: number) {
      return a > b;
    },
  },
  [OPERATOR_LT]: {
    id: OPERATOR_LT,
    name: 'lt',
    humanName: 'is less than',
    apply: function (a: number, b: number) {
      return a < b;
    },
  },
  [OPERATOR_GTE]: {
    id: OPERATOR_GTE,
    name: 'gte',
    humanName: 'is greater than or equal to',
    format: function (options) {
      const {type, value, value2, value3} = options;

      if (type !== 'Date') {
        return {operatorName: 'gte', value: value ?? ''};
      }

      if (value === 0) {
        return {operatorName: 'gte', value: 'today'};
      }

      return {
        operatorName: 'gte',
        value: `${String(value)} ${String(value2)}${
          value3 ? ` in the ${String(value3).toLowerCase()}` : ''
        }`,
      };
    },
    apply: function (a: number, b: number) {
      return a >= b;
    },
  },
  [OPERATOR_LTE]: {
    id: OPERATOR_LTE,
    name: 'lte',
    humanName: 'is less than or equal to',
    format: function (options) {
      const {type, value, value2, value3} = options;

      if (type !== 'Date') {
        return {operatorName: 'lte', value: value ?? ''};
      }

      if (value === 0) {
        return {operatorName: 'lte', value: 'end of today'};
      }

      return {
        operatorName: 'lte',
        value: `${String(value)} ${String(value2)}${
          value3 ? ` in the ${String(value3).toLowerCase()}` : ''
        }`,
      };
    },
    apply: function (a: number, b: number) {
      return a <= b;
    },
  },
  [OPERATOR_EXISTS]: {
    id: OPERATOR_EXISTS,
    value: 'yes',
    name: 'exists',
    humanName: 'exists',
    apply: function (a: unknown) {
      return a != undefined; // eslint-disable-line eqeqeq
    },
  },
  [OPERATOR_NOT_EXISTS]: {
    id: OPERATOR_NOT_EXISTS,
    value: 'no',
    name: 'exists',
    humanName: 'does not exist',
    apply: function (a: unknown) {
      return a != undefined; // eslint-disable-line eqeqeq
    },
  },
  [OPERATOR_IDIN]: {
    id: OPERATOR_IDIN,
    name: 'idin',
    humanName: 'contains',
    apply: applyInvalidOperator('idin'),
  },
  [OPERATOR_IDNIN]: {
    id: OPERATOR_IDNIN,
    name: 'idnin',
    humanName: 'does not contain',
    apply: applyInvalidOperator('idnin'),
  },
  [OPERATOR_TRUE]: {
    id: OPERATOR_TRUE,
    value: 'true',
    name: 'eq',
    humanName: 'is true',
    apply: applyInvalidOperator('true'),
  },
  [OPERATOR_FALSE]: {
    id: OPERATOR_FALSE,
    value: 'true',
    name: 'ne',
    humanName: 'is false',
    apply: applyInvalidOperator('false'),
  },
} as const satisfies Record<
  string,
  // Fields we expect every operator to have
  {
    id: string;
    name: string;
    humanName: string;
    apply: ((...args: any) => boolean) | ((...args: any) => never);
    format?: FormatCallback;
    [key: string]: unknown;
  }
>;

export type OperatorType = (typeof OPERATORS)[keyof typeof OPERATORS];
export type OperatorId = keyof typeof OPERATORS;

export const getOperatorById = function (opId: OperatorId): OperatorType {
  return OPERATORS[opId];
};

export const getOperatorsByName = function (
  opName: string
): Array<OperatorType> {
  return Object.values(OPERATORS).filter(function (opDetails: OperatorType) {
    return opDetails.name === opName;
  });
};

export const isComparisonOperator = function (op: OperatorId) {
  return (
    op === OPERATOR_EQ ||
    op === OPERATOR_NE ||
    op === OPERATOR_LT ||
    op === OPERATOR_GT ||
    op === OPERATOR_LTE ||
    op === OPERATOR_GTE
  );
};
